import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faTwitter } from "@fortawesome/free-brands-svg-icons";

export const Ultra24HeroSection = () => {
  const [countdown, setCountdown] = useState({
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const targetDateStr = "2026-08-15T07:00:00+07:00";
    const target = new Date(targetDateStr);

    const updateCountdown = () => {
      const now = new Date();
      let months = 0;
      let tempTarget = new Date(now);

      while (tempTarget < target) {
        tempTarget.setMonth(tempTarget.getMonth() + 1);
        if (tempTarget <= target) months++;
        else break;
      }
      tempTarget.setMonth(now.getMonth() + months);

      if (tempTarget > target) {
        months--;
        tempTarget = new Date(now);
        tempTarget.setMonth(now.getMonth() + months);
      }

      const timeLeft = target.getTime() - tempTarget.getTime();
      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setCountdown({ months, days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const scrollToNextSection = () => {
    const nextSection = document.getElementById("event-overview");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const socialMedia = [
    { icon: faFacebook, link: "#" },
    { icon: faInstagram, link: "https://www.instagram.com/indonesiaultra24/" },
    { icon: faTwitter, link: "#" },
  ];

  return (
    <section
      id="hero"
      className="w-full -mt-20 pt-20 min-h-screen text-white text-center flex flex-col justify-center relative"
    >

      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4">
        {socialMedia.map((social, index) => (
          <motion.a
            key={index}
            href={social.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white text-xl sm:text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:text-red-200 transition-colors"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 + index * 0.1, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon icon={social.icon} />
          </motion.a>
        ))}
      </div>

      <div className="relative text-white text-center px-6 z-1 flex flex-col justify-center flex-grow mt-2 md:mt-24 md:pb-24 gap-2">
        <motion.img
          src="/logo/logowhite.svg"
          alt="Ultra24 Logo"
          className="h-36 md:h-52 mx-auto object-contain"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
          viewport={{ once: true }}
        />
        <motion.p
          className="text-2xl md:text-5xl lg:text-6xl pt-4 font-bold mx-auto text-white"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
          viewport={{ once: true }}
        >
          INDONESIA ULTRA 24
        </motion.p>
        <motion.p
          className="text-sm md:text-4xl lg:text-3xl pb-10 font-normal mx-auto text-white"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
          viewport={{ once: true }}
        >
          EVERY LAP TELLS A STORY
        </motion.p>

        <div className="grid grid-cols-5 gap-2 mb-8 text-center max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-black text-white">{countdown.months}</p>
            <p className="text-sm md:text-base text-white/80">Months</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-black text-white">{countdown.days}</p>
            <p className="text-sm md:text-base text-white/80">Days</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-black text-white">{countdown.hours}</p>
            <p className="text-sm md:text-base text-white/80">Hours</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-black text-white">{countdown.minutes}</p>
            <p className="text-sm md:text-base text-white/80">Minutes</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-2xl md:text-3xl font-black text-white">{countdown.seconds}</p>
            <p className="text-sm md:text-base text-white/80">Seconds</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer z-10"
        onClick={scrollToNextSection}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <FontAwesomeIcon icon={faChevronDown} className="text-white text-2xl" />
        </motion.div>
      </motion.div>
    </section>
  );
};
